import React from 'react';
import {format, timezoneName} from '../../utils/date';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import type z from 'zod';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {getReasonFormSchema} from '../reason-form/reason-form.schema';

import {RootMachineContext} from '../../machines/rootMachine/rootMachine.context';
import {selectEventDetails} from '../../selectors/event-details-selector';
import {
  selectError,
  selectIsRescheduleFromContext,
  selectRescheduleLimit,
} from '../../selectors/commonSelector';
import {CardChildren} from '../../lib/card-children/card-children';
import {SmallCard} from '../../lib/small-card/small-card';
import {TextareaInput} from '../../lib/textarea-input';

import {
  BodyMediumBold,
  BodySmall,
  BodySmallBold,
  Button,
  InContextAlert,
  Link,
  SemanticIcon,
  TruncatedTextWithTooltip,
} from '@spaced-out/ui-design-system';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';

import {ReasonInput} from '../reason-form/reason-input';
import {selectReason} from '../../selectors/cancellation-selector';

import css from './booking-success.css';


export const BookingSuccess = () => {
  const {t, language} = useLabelTranslation();
  const rootRef = RootMachineContext.useActorRef();
  const state = RootMachineContext.useSelector((state) => state);
  const eventDetails = RootMachineContext.useSelector(selectEventDetails);
  const error = RootMachineContext.useSelector((state) =>
    selectError(state, t),
  );

  const isCancelSubmitting = state.matches('bookingCancelSubmitting');

  const rescheduleLimit = RootMachineContext.useSelector(selectRescheduleLimit);
  const isReschedule = RootMachineContext.useSelector(
    selectIsRescheduleFromContext,
  );
  const reason = RootMachineContext.useSelector(selectReason);

  const showRescheduleLimit = rescheduleLimit > -1 && isReschedule;

  if (!eventDetails) {
    return null;
  }

  const startDate = new Date(eventDetails.event_start);
  const endDate = new Date(eventDetails.event_end);

  const formattedDate = format(startDate, 'PP', {language});
  const startTime = format(startDate, 'p', {language});
  const endTime = format(endDate, 'p', {language});
  const formattedTime = `${startTime} - ${endTime}`;

  const reasonSchema = getReasonFormSchema(t);

  const {control, handleSubmit, formState} = useForm<
    z.infer<typeof reasonSchema>
  >({
    mode: 'onTouched',
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(reasonSchema),
  });

  const timezone = timezoneName(eventDetails.event_start, language);

  const details = [
    {
      label: t('Host', 'Host'),
      value: (
        <BodySmallBold>
          <TruncatedTextWithTooltip
            line={2}
            tooltip={{
              bodyMaxLines: 4,
            }}
          >
            {/* name is not translated */}
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {eventDetails.host_name}
          </TruncatedTextWithTooltip>
        </BodySmallBold>
      ),
      icon: 'user',
    },
    {
      label: t('Date', 'Date'),
      // eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels
      value: <BodySmallBold>{formattedDate}</BodySmallBold>,
      icon: 'calendar',
    },
    {
      label: t('Time', 'Time'),
      value: (
        <div className={css.timeValue}>
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          <BodySmallBold>{formattedTime}</BodySmallBold>
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          <BodySmall>{timezone || ''}</BodySmall>
        </div>
      ),
      icon: 'clock',
    },
    {
      label: t('Location', 'Location'),
      value: (
        <BodySmallBold>
          <TruncatedTextWithTooltip
            line={2}
            tooltip={{
              bodyMaxLines: 4,
            }}
          >
            {/* location is a user input */}
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {eventDetails.location || t('NA', 'N/A')}
          </TruncatedTextWithTooltip>
        </BodySmallBold>
      ),
      icon: 'map-pin',
    },
  ];

  const submitForm = (values: z.infer<typeof reasonSchema>) => {
    rootRef.send({
      type: 'bookingCancelForm.submit',
      reason: values.reason || '',
    });
  };

  const isCancel = state.matches('bookingCancelForm');
  const {errorMessage, allowRetry, isError} = error;

  return (
    <SmallCard
      title={
        isReschedule
          ? t(
              'Your_meeting_has_been_rescheduled_successfully',
              'Your meeting has been rescheduled successfully',
            )
          : `${t(
              'Thank_you_your_meeting_is_confirmed',
              'Thank you, your meeting is confirmed',
            )}!`
      }
      icon="badge-check"
      iconSemantic="success"
    >
      <div className={css.detailsWrapper}>
        {showRescheduleLimit && (
          <InContextAlert semantic="success">{`${t(
            'Your_meeting_has_been_rescheduled_You_have_rescheduleLimit_reschedules_left',
            {rescheduleLimit},
          )}. `}</InContextAlert>
        )}
        <CardChildren>
          <div className={css.details}>
            {details.map(({icon, label, value}) => (
              <div key={label} className={css.detailItem}>
                <div className={css.detailItemLabel}>
                  <SemanticIcon
                    size="small"
                    name={icon}
                    semantic="neutral"
                    type="solid"
                  />
                  {/* label is already translated */}
                  {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                  <BodyMediumBold>{label}</BodyMediumBold>
                </div>
                {/* value is a react component */}
                {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                <div className={css.detailItemValue}>{value}</div>
              </div>
            ))}
          </div>
        </CardChildren>
        <CardChildren>
          {isCancel || isCancelSubmitting || isError ? (
            <div className={css.cancelForm}>
              {isError && (
                <InContextAlert semantic="danger">
                  {/* Note: already getting translated from errorText */}
                  {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                  {errorMessage}
                </InContextAlert>
              )}
              <ReasonInput
                control={control}
                reasonName="reason"
                required={true}
                disabled={isCancelSubmitting}
                actionType="cancel"
                participantType="CANDIDATE"
              />
              <Button
                onClick={handleSubmit(submitForm)}
                isLoading={isCancelSubmitting}
                disabled={!formState.isValid || !allowRetry}
              >
                {t('Cancel', 'Cancel')}
              </Button>
            </div>
          ) : (
            <div className={css.successFooterContainer}>
              {isReschedule && (
                <Textarea
                  locked={true}
                  value={reason || ''}
                  label={t('Reason_for_reschedule', 'Reason for reschedule')}
                />
              )}
              <div className={css.successFooter}>
                <BodySmall>
                  {t('Need_to_Make_Changes', 'Need to Make Changes')}?
                </BodySmall>
                <Link onClick={() => rootRef.send({type: 'reschedule.go'})}>
                  {t('Reschedule', 'Reschedule')}
                </Link>
                <BodySmall>{t('or', 'or')}</BodySmall>
                <Link onClick={() => rootRef.send({type: 'cancellation.go'})}>
                  {t('Cancel', 'Cancel')}
                </Link>
              </div>
            </div>
          )}
        </CardChildren>
      </div>
    </SmallCard>
  );
};
